import React, { useState, useEffect, useRef } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import PricingTable from '@/components/PricingTable';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSlider from '@/components/ProcessSlider';
import ContactPopup from '@/components/ContactPopup';
import MobileSlider from '@/components/MobileSlider';
import { Shield, FileCheck, Building, Search, Package, Award, Clock, Users, Zap, Star, TrendingUp, CheckCircle, ArrowRight, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home = () => {
  const [showPopup, setShowPopup] = useState(false);
  
  // Single trigger for all counters
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);

  // Counter hooks that start when stats section is visible
  const [count1, setCount1] = useState(0);
  const [count2, setCount2] = useState(0);
  const [count3, setCount3] = useState(0);
  const [count4, setCount4] = useState(0);

  // Intersection observer for stats section
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !statsVisible) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [statsVisible]);

  // Animate counters when visible
  useEffect(() => {
    if (!statsVisible) return;

    const animateCounter = (end, duration, setCount) => {
      let startTime;
      let animationFrame;

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * end));

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      };

      animationFrame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrame);
    };

    const cleanup1 = animateCounter(10000, 2500, setCount1);
    const cleanup2 = animateCounter(98, 2000, setCount2);
    const cleanup3 = animateCounter(50, 2000, setCount3);
    const cleanup4 = animateCounter(24, 1500, setCount4);

    return () => {
      cleanup1();
      cleanup2();
      cleanup3();
      cleanup4();
    };
  }, [statsVisible]);

  const services = [
    {
      icon: Shield,
      title: 'Trademark Registration',
      description: 'Secure your brand identity with comprehensive trademark registration services across all 50 states and federal level.',
      link: '/services/trademark-registration',
      gradient: 'from-indigo-500 to-purple-600',
    },
    {
      icon: FileCheck,
      title: 'Copyright Registration',
      description: 'Protect your creative works, intellectual property, and original content with our expert copyright registration services.',
      link: '/services/copyright-registration',
      gradient: 'from-purple-500 to-pink-600',
    },
    {
      icon: Building,
      title: 'Company Formation',
      description: 'Start your business the right way with our complete company formation and entity structuring services.',
      link: '/services/company-formation',
      gradient: 'from-cyan-500 to-blue-600',
    },
    {
      icon: Search,
      title: 'Trademark Search',
      description: 'Comprehensive trademark searches to identify potential conflicts before you invest in your brand.',
      link: '/services/trademark-search',
      gradient: 'from-amber-500 to-orange-600',
    },
    {
      icon: Package,
      title: 'Amazon Brand Registry',
      description: 'Enhance your Amazon presence and protect your products with our Amazon Brand Registry assistance.',
      link: '/services/amazon-brand-registry',
      gradient: 'from-emerald-500 to-teal-600',
    },
    {
      icon: Zap,
      title: 'Get Started Today',
      description: 'Ready to protect your brand? Schedule a free consultation with our trademark experts and secure your business identity.',
      link: null,
      gradient: 'from-rose-500 to-pink-600',
      isCTA: true,
    },
  ];

  const features = [
    {
      icon: Award,
      title: '98% Success Rate',
      description: 'Industry-leading approval rate',
      color: 'indigo',
    },
    {
      icon: Clock,
      title: 'Fast Processing',
      description: 'Quick turnaround times',
      color: 'purple',
    },
    {
      icon: Users,
      title: 'Expert Support',
      description: 'Professional guidance',
      color: 'cyan',
    },
    {
      icon: Zap,
      title: 'Instant Updates',
      description: 'Real-time notifications',
      color: 'amber',
    },
  ];

  const stats = [
    { number: '10,000+', label: 'Trademarks Registered', icon: Star },
    { number: '98%', label: 'Success Rate', icon: TrendingUp },
    { number: '50', label: 'States Covered', icon: Shield },
    { number: '24/7', label: 'Support Available', icon: Users },
  ];

  // FAQ Item Component
  const FAQItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors hover:bg-gray-50"
        >
          <span className="font-semibold text-lg pr-4" style={{ color: 'var(--text-dark)' }}>
            {question}
          </span>
          <ChevronDown 
            className={`text-indigo-600 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
            size={24} 
          />
        </button>
        <div 
          className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}
        >
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
            <p className="text-gray-600 leading-relaxed">{answer}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <Navigation />

      {/* Creative Hero Section with Diagonal Split */}
      <section
        className="relative min-h-screen flex items-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
          <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
        </div>

        {/* Diagonal Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 to-purple-600/5" 
             style={{ clipPath: 'polygon(0 0, 100% 0, 100% 85%, 0 100%)' }}></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* Mobile: Image First, Desktop: Content First */}
            <div className="order-2 lg:order-1 text-left w-full">
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-full">
                  Trusted by 10,000+ Businesses
                </span>
              </div>
              
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
                data-testid="hero-title"
              >
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Protect Your
                </span>
                <br />
                <span style={{ color: 'var(--text-dark)' }}>Brand Legacy</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-xl">
                Professional trademark registration and brand protection services. 
                Secure your business identity with confidence and expert guidance.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button
                  onClick={() => setShowPopup(true)}
                  className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all"
                  data-testid="hero-get-started-button"
                >
                  Get Started Today
                </button>
                <button
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-4 border-2 border-indigo-600 text-indigo-600 font-semibold rounded-xl hover:bg-indigo-50 transform hover:-translate-y-1 transition-all"
                  data-testid="hero-learn-more-button"
                >
                  Explore Services
                </button>
              </div>

              {/* Quick Features */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-indigo-600" size={20} />
                  <span className="text-sm text-gray-600">Nationwide Protection</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-purple-600" size={20} />
                  <span className="text-sm text-gray-600">98% Success Rate</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-cyan-600" size={20} />
                  <span className="text-sm text-gray-600">Expert Guidance</span>
                </div>
              </div>
            </div>

            {/* Mobile: Show First (Image), Desktop: Show Second */}
            <div className="order-1 lg:order-2 relative w-full">
              <div className="relative z-10">
                <img
                  src="https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a"
                  alt="Professional business meeting"
                  className="rounded-3xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                  data-testid="hero-image"
                />
                
                {/* Floating Card */}
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-2xl max-w-xs">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <Shield className="text-white" size={24} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">10,000+</div>
                      <div className="text-sm text-gray-600">Protected Brands</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section with Creative Cards */}
      <section className="section-padding bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600" data-testid="stats-section" ref={statsRef}>
        <div className="container mx-auto px-4 text-center text-white">
          {/* Desktop: Grid, Mobile/Tablet: Slider */}
          <div className="hidden lg:grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="group">
              <div className="text-5xl font-bold mb-2 group-hover:scale-110 transition-transform">
                {count1.toLocaleString()}+
              </div>
              <div className="text-indigo-100">Businesses Served</div>
            </div>
            <div className="group">
              <div className="text-5xl font-bold mb-2 group-hover:scale-110 transition-transform">
                {count2}%
              </div>
              <div className="text-indigo-100">Success Rate</div>
            </div>
            <div className="group">
              <div className="text-5xl font-bold mb-2 group-hover:scale-110 transition-transform">
                {count3}
              </div>
              <div className="text-indigo-100">States Covered</div>
            </div>
            <div className="group">
              <div className="text-5xl font-bold mb-2 group-hover:scale-110 transition-transform">
                {count4}/7
              </div>
              <div className="text-indigo-100">Support Available</div>
            </div>
          </div>

          {/* Mobile/Tablet Slider */}
          <div className="lg:hidden">
            <MobileSlider autoplay={true} autoplayDelay={3000}>
              <div className="group py-8">
                <div className="text-5xl font-bold mb-2">{count1.toLocaleString()}+</div>
                <div className="text-indigo-100">Businesses Served</div>
              </div>
              <div className="group py-8">
                <div className="text-5xl font-bold mb-2">{count2}%</div>
                <div className="text-indigo-100">Success Rate</div>
              </div>
              <div className="group py-8">
                <div className="text-5xl font-bold mb-2">{count3}</div>
                <div className="text-indigo-100">States Covered</div>
              </div>
              <div className="group py-8">
                <div className="text-5xl font-bold mb-2">{count4}/7</div>
                <div className="text-indigo-100">Support Available</div>
              </div>
            </MobileSlider>
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-indigo-50 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            
            {/* Mobile: Image First, Desktop: Content First */}
            <div className="order-2 lg:order-1 lg:w-1/2 space-y-6 w-full">
              <div className="inline-block px-4 py-2 bg-indigo-100 rounded-full mb-2">
                <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">Your Trusted Partner</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold leading-tight" style={{ color: 'var(--text-dark)' }}>
                Protect Your Brand with <span className="text-indigo-600">Confidence</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We understand that your brand is more than just a name—it's your business identity, your reputation, and your future. With over 10,000 successful trademark registrations, we provide comprehensive protection services tailored to your unique needs.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our expert team guides you through every step of the trademark process, ensuring your intellectual property is secured efficiently and effectively.
              </p>
              <button 
                onClick={() => setShowPopup(true)}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all group"
              >
                Start Your Protection Today
                <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" size={20} />
              </button>
            </div>

            {/* Mobile: Show First (Image), Desktop: Show Second */}
            <div className="order-1 lg:order-2 lg:w-1/2 w-full">
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-indigo-200 rounded-full blur-3xl opacity-60"></div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-purple-200 rounded-full blur-3xl opacity-60"></div>
                <img 
                  src="https://images.unsplash.com/photo-1599090738077-75d2187fd892?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8Ymx1ZXwxNzYyODA4NTQwfDA&ixlib=rb-4.1.0&q=85" 
                  alt="Professional Business Protection" 
                  className="relative rounded-3xl shadow-2xl w-full h-auto object-cover border-8 border-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section with Asymmetric Layout */}
      <section className="section-padding bg-white relative overflow-hidden" data-testid="features-section">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-indigo-50 to-transparent"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">Why Choose Us</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-2" style={{ color: 'var(--text-dark)' }}>
              Your Trademark Protection Partner
            </h2>
          </div>

          {/* Desktop Grid View */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const bgColorClass = 
                feature.color === 'indigo' ? 'bg-indigo-500' :
                feature.color === 'purple' ? 'bg-purple-500' :
                feature.color === 'cyan' ? 'bg-cyan-500' :
                feature.color === 'amber' ? 'bg-amber-500' : 'bg-indigo-500';
              
              return (
                <div 
                  key={index} 
                  className="group relative bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-indigo-200 hover:shadow-xl transition-all transform hover:-translate-y-2"
                  data-testid={`feature-${index}`}
                >
                  <div className={`w-14 h-14 ${bgColorClass} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--text-dark)' }}>
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>

          {/* Mobile Slider View */}
          <div className="md:hidden relative">
            <div className="overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory flex gap-4 scrollbar-hide">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                const bgColorClass = 
                  feature.color === 'indigo' ? 'bg-indigo-500' :
                  feature.color === 'purple' ? 'bg-purple-500' :
                  feature.color === 'cyan' ? 'bg-cyan-500' :
                  feature.color === 'amber' ? 'bg-amber-500' : 'bg-indigo-500';
                
                return (
                  <div 
                    key={index} 
                    className="flex-shrink-0 w-[85vw] snap-center bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-lg"
                    data-testid={`feature-mobile-${index}`}
                  >
                    <div className={`w-14 h-14 ${bgColorClass} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--text-dark)' }}>
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>
            <div className="text-center text-sm text-gray-500 mt-4">
              ← Swipe to see more →
            </div>
          </div>
        </div>
      </section>

      {/* Services Section with Grid Layout */}
      <section id="services" className="section-padding relative overflow-hidden bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50" data-testid="services-section">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 bg-indigo-100 rounded-full mb-4">
              <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight" style={{ color: 'var(--text-dark)' }}>
              Complete Brand Protection Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive trademark services tailored to protect and grow your business
            </p>
          </div>

          {/* Services Grid - Desktop */}
          <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              
              return (
                <div
                  key={index}
                  className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border-2 border-gray-100 hover:border-indigo-200"
                  data-testid={`service-${index}`}
                >
                  {/* Icon Header */}
                  <div className={`bg-gradient-to-br ${service.gradient} p-8 text-center relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-white/10 group-hover:bg-white/20 transition-colors"></div>
                    <div className="relative z-10">
                      <div className="w-20 h-20 bg-white/30 backdrop-blur-lg rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                        <Icon className="text-white" size={40} />
                      </div>
                      <h3 className="text-2xl font-bold text-white">
                        {service.title}
                      </h3>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8">
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    {service.isCTA ? (
                      <button
                        onClick={() => setShowPopup(true)}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all group/link"
                      >
                        Schedule Consultation
                        <ArrowRight className="ml-2 group-hover/link:translate-x-2 transition-transform" size={20} />
                      </button>
                    ) : (
                      <Link
                        to={service.link}
                        className="inline-flex items-center text-indigo-600 font-semibold hover:text-purple-600 transition-colors group/link"
                      >
                        Learn More
                        <ArrowRight className="ml-2 group-hover/link:translate-x-2 transition-transform" size={20} />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Services Slider - Mobile/Tablet */}
          <div className="lg:hidden">
            <MobileSlider>
              {services.map((service, index) => {
                const Icon = service.icon;
                
                return (
                  <div
                    key={index}
                    className="bg-white rounded-3xl shadow-lg overflow-hidden border-2 border-gray-100 mx-2"
                  >
                    {/* Icon Header */}
                    <div className={`bg-gradient-to-br ${service.gradient} p-6 text-center relative`}>
                      <div className="relative z-10">
                        <div className="w-16 h-16 bg-white/30 backdrop-blur-lg rounded-2xl flex items-center justify-center mx-auto mb-3">
                          <Icon className="text-white" size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-white">
                          {service.title}
                        </h3>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      {service.isCTA ? (
                        <button
                          onClick={() => setShowPopup(true)}
                          className="w-full inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-full"
                        >
                          Schedule Consultation
                          <ArrowRight className="ml-2" size={20} />
                        </button>
                      ) : (
                        <Link
                          to={service.link}
                          className="w-full inline-flex items-center justify-center px-6 py-3 bg-indigo-50 text-indigo-600 font-semibold rounded-full"
                        >
                          Learn More
                          <ArrowRight className="ml-2" size={20} />
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </MobileSlider>
          </div>
        </div>
      </section>

      {/* Process Section with Creative Timeline */}
      <section className="section-padding bg-white" data-testid="process-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">How It Works</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-4" style={{ color: 'var(--text-dark)' }}>
              Simple 3-Step Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Your trademark secured in three easy steps
            </p>
          </div>

          <ProcessSlider />
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative overflow-hidden py-24" data-testid="cta-banner">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1557682268-e3955ed5d83f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdyYWRpZW50fGVufDB8fHxwdXJwbGV8MTc2MjgwOTI5NHww&ixlib=rb-4.1.0&q=85" 
            alt="Abstract Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/90 via-purple-900/85 to-pink-900/90"></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Ready to Protect Your Brand?
            </h2>
            <p className="text-xl sm:text-2xl text-white/90 mb-10 leading-relaxed">
              Join thousands of businesses who trust us to secure their trademarks. Get started today and safeguard your brand identity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={() => setShowPopup(true)}
                className="px-10 py-5 bg-white text-indigo-600 font-bold text-lg rounded-full hover:shadow-2xl hover:scale-105 transition-all transform"
              >
                Get Started Now
              </button>
              <Link
                to="/services/trademark-registration"
                className="px-10 py-5 bg-transparent border-4 border-white text-white font-bold text-lg rounded-full hover:bg-white hover:text-indigo-600 transition-all transform hover:scale-105"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 w-20 h-20 border-4 border-white/20 rounded-full"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 border-4 border-white/10 rounded-full"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/10 rounded-lg rotate-45"></div>
      </section>

      {/* Pricing Section with Creative Design */}
      <section className="section-padding bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-hidden" data-testid="pricing-section">
        {/* Decorative shapes */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-200 rounded-full opacity-20 blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-white rounded-full shadow-md mb-4">
              <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">Pricing Plans</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-4" style={{ color: 'var(--text-dark)' }}>
              Choose Your Perfect Plan
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Transparent pricing with no hidden fees. Select the package that fits your business needs.
            </p>
          </div>

          <PricingTable />

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Not sure which plan is right for you?</p>
            <button
              onClick={() => setShowPopup(true)}
              className="px-8 py-3 bg-white text-indigo-600 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all border-2 border-indigo-600 hover:bg-indigo-50"
            >
              Talk to an Expert
            </button>
          </div>
        </div>
      </section>

      {/* Informative Section */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            
            {/* Mobile: Image First, Desktop: Content First */}
            <div className="order-2 lg:order-1 lg:w-1/2 space-y-6 w-full">
              <div className="inline-block px-4 py-2 bg-indigo-100 rounded-full mb-2">
                <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">Why Choose Us</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold leading-tight" style={{ color: 'var(--text-dark)' }}>
                Trusted Experts in <span className="text-indigo-600">Brand Protection</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                In today's competitive marketplace, your brand identity is one of your most valuable assets. Without proper trademark protection, you risk losing control of your brand to competitors or copycats.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="text-indigo-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Protect your brand from unauthorized use</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-indigo-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Establish exclusive rights to your business name</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-indigo-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Increase your brand's value and credibility</span>
                </li>
              </ul>
              <button 
                onClick={() => setShowPopup(true)}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all group mt-4"
              >
                Protect Your Brand Now
                <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" size={20} />
              </button>
            </div>

            {/* Mobile: Show First (Image), Desktop: Show Second */}
            <div className="order-1 lg:order-2 lg:w-1/2 relative flex items-end w-full">
              <div className="relative grid grid-cols-2 gap-4 w-full">
                {/* First Image - Corporate Building, positioned slightly up */}
                <div className="relative transform -translate-y-8">
                  <div className="absolute -top-4 -left-4 w-20 h-20 bg-indigo-200 rounded-full blur-2xl opacity-60"></div>
                  <img 
                    src="https://images.unsplash.com/photo-1704423846283-f92ff6badea3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZ3xlbnwwfHx8Ymx1ZXwxNzYyODEwNTM2fDA&ixlib=rb-4.1.0&q=85" 
                    alt="Corporate Building" 
                    className="relative rounded-2xl shadow-2xl w-full h-80 object-cover border-4 border-white"
                  />
                </div>
                
                {/* Second Image - Office, positioned slightly down, aligned to bottom */}
                <div className="relative">
                  <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-purple-200 rounded-full blur-2xl opacity-60"></div>
                  <img 
                    src="https://images.unsplash.com/photo-1544717297-fa95b6ee9643?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2V8ZW58MHx8fGJsdWV8MTc2MjgxMDMyNHww&ixlib=rb-4.1.0&q=85" 
                    alt="Professional Office" 
                    className="relative rounded-2xl shadow-2xl w-full h-80 object-cover border-4 border-white"
                  />
                </div>
              </div>
              
              {/* Decorative element */}
              <div className="absolute -top-8 -right-8 w-32 h-32 border-4 border-indigo-200 rounded-full opacity-30"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-white" data-testid="testimonials-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">Testimonials</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-4" style={{ color: 'var(--text-dark)' }}>
              Trusted by Thousands
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See what our clients say about us
            </p>
          </div>

          <TestimonialSlider />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Left side - FAQs */}
            <div className="lg:w-1/2">
              <div className="mb-8">
                <div className="inline-block px-4 py-2 bg-indigo-100 rounded-full mb-4">
                  <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">FAQs</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: 'var(--text-dark)' }}>
                  Frequently Asked Questions
                </h2>
                <p className="text-lg text-gray-600">
                  Find answers to common questions about trademark registration and our services.
                </p>
              </div>

              {/* FAQ Accordion */}
              <div className="space-y-4">
                {[
                  {
                    question: 'How long does the trademark registration process take?',
                    answer: 'The trademark registration process typically takes 8-12 months from filing to approval. However, the timeline can vary depending on the complexity of your application and any office actions that may arise.'
                  },
                  {
                    question: 'What is included in your trademark registration service?',
                    answer: 'Our service includes a comprehensive trademark search, application preparation and filing, monitoring of your application status, responding to office actions, and final registration certificate delivery.'
                  },
                  {
                    question: 'Do I need a trademark attorney?',
                    answer: 'While not legally required, having a trademark attorney significantly increases your chances of successful registration. Our experts ensure proper classification, avoid common pitfalls, and handle any legal complexities.'
                  },
                  {
                    question: 'What happens if my trademark application is rejected?',
                    answer: 'If your application receives an office action or rejection, we will work with you to address the issues. This may involve responding to examiner concerns, amending your application, or exploring alternative strategies.'
                  },
                  {
                    question: 'Can I trademark a business name that is already in use?',
                    answer: 'It depends on various factors including the industry, geographic location, and whether the name is already registered. We conduct thorough searches to identify potential conflicts before filing your application.'
                  }
                ].map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>

            {/* Right side - Sticky Aligned Image */}
            <div className="lg:w-1/2 flex items-stretch">
              <div className="relative w-full">
                <div className="sticky top-8">
                  <div className="absolute -top-6 -right-6 w-32 h-32 bg-indigo-200 rounded-full blur-3xl opacity-60"></div>
                  <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-purple-200 rounded-full blur-3xl opacity-60"></div>
                  <img 
                    src="https://images.unsplash.com/photo-1560250163-17506787d971?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fGJsdWV8MTc2MjgxMTU1MXww&ixlib=rb-4.1.0&q=85" 
                    alt="Business Consultation Meeting" 
                    className="relative rounded-3xl shadow-2xl w-full h-full object-cover border-8 border-white"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Creative Design */}
      <section
        className="section-padding relative overflow-hidden"
        data-testid="cta-section"
      >
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600"></div>
        
        {/* Animated Shapes */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-white">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Join thousands of businesses who trust us with their brand protection
          </p>
          <button
            onClick={() => setShowPopup(true)}
            className="px-10 py-5 bg-white text-indigo-600 font-bold rounded-xl hover:bg-gray-100 shadow-2xl transform hover:scale-105 transition-all"
            data-testid="cta-button"
          >
            Register My Trademark
          </button>
        </div>
      </section>

      <Footer />

      {showPopup && <ContactPopup onClose={() => setShowPopup(false)} source="popup" />}
    </div>
  );
};

export default Home;